import type { LabelType } from './label-types';

/**
 * Static configuration for the Library's explore carousels. Each entry
 * maps to one horizontal strip on the page; the carousel component turns
 * the `labelType` + `values` into a filtered `GET /books?randomize=true`
 * request and renders the result.
 *
 * Values must match an alias in the seeded `book_labels` catalogue (see
 * `server/prisma/seed.ts`). If a seed label changes, update the matching
 * entry here so the carousel keeps returning results.
 */
export interface CarouselConfig {
  /** Stable id used as the React key and for telemetry / scroll anchors. */
  id: string;
  title: string;
  labelType: LabelType;
  /** Array of aliases; passed to the server as repeated query params. */
  values: string[];
}

/**
 * Ordered list of 14 carousels. The Library page shuffles this on mount
 * and reveals carousels one at a time as the user scrolls — the shuffle
 * keeps the landing view feeling fresh without needing a server mix.
 */
export const CAROUSEL_CONFIGS: CarouselConfig[] = [
  {
    id: 'top-picks-adventure',
    title: 'Top Picks on Adventure Books',
    labelType: 'genre',
    values: ['Adventure'],
  },
  {
    id: 'classic-must-reads',
    title: 'Classic Must Reads',
    labelType: 'genre',
    values: ['Fairy Tales / Folklore / Fable'],
  },
  {
    id: 'solve-a-mystery',
    title: 'Solve a Mystery',
    labelType: 'genre',
    values: ['Mystery'],
  },
  {
    id: 'fantasy-worlds',
    title: 'Step Into Fantasy Worlds',
    labelType: 'genre',
    values: ['Fantasy'],
  },
  {
    id: 'farm-life',
    title: 'Books on Farm Life',
    labelType: 'topic',
    values: ['Farmer / Farm Life'],
  },
  {
    id: 'ocean-nature',
    title: 'Nature & the Wild',
    labelType: 'topic',
    values: ['Nature / Science'],
  },
  {
    id: 'friendship-family',
    title: 'Friendship & Family Tales',
    labelType: 'topic',
    values: ['Friendship', 'Family'],
  },
  {
    id: 'inventors-explorers',
    title: 'Inventors & Explorers',
    labelType: 'topic',
    values: ['Inventor / Engineer', 'Explorers', 'Historical Figures'],
  },
  {
    id: 'american-history',
    title: 'American History Books',
    labelType: 'historical_event',
    values: ['Revolutionary War', 'Civil War', 'American Revolution', 'Civil Rights Movement'],
  },
  {
    id: 'world-wars',
    title: 'World Wars & Modern History',
    labelType: 'historical_event',
    values: ['World War I', 'World War II', 'Moon Landing'],
  },
  {
    id: 'medieval-ancient',
    title: 'Ancient & Medieval Times',
    labelType: 'time_period',
    values: ['Ancient Times', 'Medieval Times', 'Renaissance'],
  },
];
